/*
  Swaraas — reel carousel behaviour.
  Coverflow with video playback, image support, and automatic smooth swipe after video finishes.
*/

class ReelCarousel extends HTMLElement {
  connectedCallback() {
    this.rail = this.querySelector('[data-reel-rail]');

    // A re-render in the theme editor can hand back an element that still
    // carries a previous run's clones. Drop them before measuring anything.
    this.querySelectorAll('[data-reel-clone]').forEach((node) => node.remove());

    this.cards = Array.from(this.querySelectorAll('[data-reel-card]'));

    if (!this.rail || this.cards.length === 0) return;

    this.buildLoop();

    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.prevBtn = this.querySelector('[data-reel-prev]');
    this.nextBtn = this.querySelector('[data-reel-next]');

    this.onNav = this.onNav.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onVisibility = this.onVisibility.bind(this);
    this.onVideoEnded = this.onVideoEnded.bind(this);
    this.onMouseEnter = this.onMouseEnter.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);

    this.isHovered = false;
    this.inView = false;
    this.autoTimer = null;

    this.classList.add('sw-reel--enhanced');

    this.watchSection();
    this.bindCardClicks();

    this.rail.addEventListener('scroll', this.onScroll, { passive: true });
    this.addEventListener('mouseenter', this.onMouseEnter);
    this.addEventListener('mouseleave', this.onMouseLeave);
    document.addEventListener('visibilitychange', this.onVisibility);

    if (this.prevBtn) this.prevBtn.addEventListener('click', this.onNav);
    if (this.nextBtn) this.nextBtn.addEventListener('click', this.onNav);

    this.syncNav();

    // Start on the first card of the MIDDLE set, with no animation, so the
    // rail opens with a full run of cards visible on both sides rather than
    // hard against its left edge.
    this.jumpToIndex(this.setStart, false);

    setTimeout(() => {
      this.syncCentre();
    }, 100);
  }

  /* --- Circular looping ----------------------------------------------------
     The rail is native overflow scrolling with scroll-snap, which has no
     concept of wrapping. Rather than fight that, the card set is rendered
     three times — [clones][real][clones] — and the shopper is kept in the
     middle copy: the moment a clone reaches centre, scrollLeft is moved by
     exactly one set width, which puts its identical twin in the same place.
     The frame before and the frame after the jump are pixel-identical, so
     there is nothing to see, and next/prev never reach an end.

     WHY NOT `next()` MODULO BACK TO INDEX 0
     That is what it did before: at the last card it scrolled the whole rail
     back to the start. Correct, but it reads as a rewind, not a loop.

     WHY CLONES DROP THEIR <video>
     Tripling the card set would otherwise triple the number of <video>
     elements the browser has to manage. Clones keep the poster <img> only —
     `play()`, `stop()` and `pauseAll()` are all null-guarded on the video, so
     a poster-only card is already a supported case. Only the real middle set
     ever plays.

     Below 4 cards there is nothing to loop through, so the section stays a
     plain rail and every method below no-ops. */
  buildLoop() {
    this.setSize = this.cards.length;
    this.loop = this.setSize >= 4;
    this.setStart = 0;

    if (!this.loop) return;

    const before = document.createDocumentFragment();
    const after = document.createDocumentFragment();

    this.cards.forEach((card) => {
      before.appendChild(this.makeClone(card));
      after.appendChild(this.makeClone(card));
    });

    this.rail.insertBefore(before, this.rail.firstChild);
    this.rail.appendChild(after);

    this.cards = Array.from(this.querySelectorAll('[data-reel-card]'));
    this.setStart = this.setSize;
    this.pendingRecentre = false;
    this.idleTimer = null;
  }

  makeClone(card) {
    const clone = card.cloneNode(true);

    clone.setAttribute('data-reel-clone', '');
    clone.setAttribute('aria-hidden', 'true');
    clone.classList.remove('is-active', 'is-playing');

    // Out of the tab order — a keyboard user must not walk the same product
    // three times, and a screen reader is already told to skip these.
    clone.querySelectorAll('a, button, [tabindex]').forEach((node) => {
      node.setAttribute('tabindex', '-1');
    });

    clone.querySelectorAll('video').forEach((node) => node.remove());

    return clone;
  }

  /* Which copy of the set a given index falls in: -1 leading, 0 middle,
     1 trailing. */
  setOf(index) {
    if (index < this.setStart) return -1;
    if (index >= this.setStart + this.setSize) return 1;
    return 0;
  }

  /* Deferred so it never fires mid-animation: a smooth scroll in flight would
     be cancelled by writing scrollLeft. Flushed once the rail goes quiet. */
  queueRecentre() {
    if (!this.loop) return;
    this.pendingRecentre = true;
  }

  flushRecentre() {
    if (!this.loop || !this.pendingRecentre) return;
    this.pendingRecentre = false;

    const index = this.cards.indexOf(this.active);
    if (index < 0) return;

    const side = this.setOf(index);
    if (side === 0) return;

    this.jumpToIndex(index - side * this.setSize, false);
  }

  /* Centre a card with no animation and without letting scroll-snap fight the
     write. Snap is suspended for the duration and a reflow is forced before it
     is restored, so it re-engages at the new position rather than dragging the
     rail back to where it was. */
  jumpToIndex(index, animate) {
    const card = this.cards[index];
    if (!card) return;

    const target = Math.max(0, card.offsetLeft + card.offsetWidth / 2 - this.rail.clientWidth / 2);

    if (animate) {
      this.rail.scrollTo({ left: target, behavior: 'smooth' });
      return;
    }

    const snap = this.rail.style.scrollSnapType;
    const behavior = this.rail.style.scrollBehavior;

    this.rail.style.scrollSnapType = 'none';
    this.rail.style.scrollBehavior = 'auto';
    this.rail.scrollLeft = target;
    void this.rail.offsetWidth;
    this.rail.style.scrollSnapType = snap;
    this.rail.style.scrollBehavior = behavior;
  }

  disconnectedCallback() {
    if (this.sectionObserver) this.sectionObserver.disconnect();
    if (this.rail) this.rail.removeEventListener('scroll', this.onScroll);
    this.removeEventListener('mouseenter', this.onMouseEnter);
    this.removeEventListener('mouseleave', this.onMouseLeave);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.clearAutoTimer();
    if (this.idleTimer) clearTimeout(this.idleTimer);
    if (this.navFrame) cancelAnimationFrame(this.navFrame);
  }

  bindCardClicks() {
    this.cards.forEach((card, index) => {
      card.addEventListener('click', (e) => {
        if (card !== this.active) {
          e.preventDefault();
          this.scrollToCard(index);
        }
      });
    });
  }

  /* WHY THIS IS NOT AN INTERSECTIONOBSERVER ANY MORE
     It used to be one, with `rootMargin: '0px -38% 0px -38%'` and
     `threshold: 0.1` — a centre band roughly a quarter of the rail wide. The
     cards overlap and are narrower than that band, so two or three of them
     satisfied it at once, and whichever entry the callback happened to visit
     last won. `this.active` was therefore routinely one or two cards off the
     card actually in the middle.

     That is what made the arrows appear to jump three at a time: `next()`
     stepped from `indexOf(this.active)`, so a stale active index compounded
     with every click.

     Nearest-centre is not a heuristic — it is the definition of which card is
     centred, so it cannot disagree with what the shopper sees. */
  centreIndex() {
    if (!this.cards || this.cards.length === 0) return -1;

    const mid = this.rail.scrollLeft + this.rail.clientWidth / 2;
    let best = 0;
    let bestGap = Infinity;

    for (let i = 0; i < this.cards.length; i += 1) {
      const card = this.cards[i];
      const gap = Math.abs(card.offsetLeft + card.offsetWidth / 2 - mid);
      if (gap < bestGap) {
        bestGap = gap;
        best = i;
      }
    }

    return best;
  }

  syncCentre() {
    const index = this.centreIndex();
    if (index < 0) return;
    this.activate(this.cards[index]);
  }

  watchSection() {
    this.sectionObserver = new IntersectionObserver(
      (entries) => {
        this.inView = entries[0].isIntersecting;
        if (this.inView) {
          if (this.active) this.play(this.active);
        } else {
          this.pauseAll();
          this.clearAutoTimer();
        }
      },
      { threshold: 0.1 }
    );

    this.sectionObserver.observe(this);
  }

  activate(card) {
    if (this.active === card) return;

    this.clearAutoTimer();

    this.cards.forEach((other) => {
      if (other !== card) {
        other.classList.remove('is-active');
        this.stop(other);
      }
    });

    card.classList.add('is-active');
    this.active = card;

    this.queueRecentre();

    if (this.inView !== false) {
      this.play(card);
    }
  }

  play(card) {
    const video = card.querySelector('video');
    this.clearAutoTimer();

    if (video) {
      if (this.reduceMotion.matches) return;

      if (video.preload !== 'auto') video.preload = 'auto';

      video.removeEventListener('ended', this.onVideoEnded);
      video.addEventListener('ended', this.onVideoEnded);

      const started = video.play();
      if (started && typeof started.catch === 'function') {
        started
          .then(() => card.classList.add('is-playing'))
          .catch(() => {
            card.classList.remove('is-playing');
            this.scheduleAutoSwipe(3500);
          });
      } else {
        card.classList.add('is-playing');
      }
    } else {
      this.scheduleAutoSwipe(3500);
    }
  }

  stop(card) {
    const video = card.querySelector('video');
    if (!video) return;

    video.removeEventListener('ended', this.onVideoEnded);
    video.pause();
    card.classList.remove('is-playing');

    try {
      video.currentTime = 0;
    } catch (error) {}
  }

  pauseAll() {
    this.cards.forEach((card) => {
      const video = card.querySelector('video');
      if (video) {
        video.removeEventListener('ended', this.onVideoEnded);
        video.pause();
      }
      card.classList.remove('is-playing');
    });
  }

  onVideoEnded() {
    if (this.inView && !this.isHovered) {
      this.next();
    }
  }

  scheduleAutoSwipe(delay) {
    this.clearAutoTimer();
    if (this.reduceMotion.matches) return;

    this.autoTimer = setTimeout(() => {
      if (this.inView && !this.isHovered) {
        this.next();
      }
    }, delay);
  }

  clearAutoTimer() {
    if (this.autoTimer) {
      clearTimeout(this.autoTimer);
      this.autoTimer = null;
    }
  }

  onMouseEnter() {
    this.isHovered = true;
  }

  onMouseLeave() {
    this.isHovered = false;
    const video = this.active ? this.active.querySelector('video') : null;
    if (!video || video.paused || video.ended) {
      this.scheduleAutoSwipe(3000);
    }
  }

  onVisibility() {
    if (document.hidden) {
      this.pauseAll();
      this.clearAutoTimer();
    } else if (this.active && this.inView !== false) {
      this.play(this.active);
    }
  }

  /* In loop mode there is no wrap-around here: stepping off the end of the
     middle set simply walks into the trailing clones, and the recentre puts
     the rail back afterwards. The modulo is the non-loop fallback. */
  next() {
    if (!this.cards || this.cards.length <= 1) return;
    const from = this.centreIndex();
    const to = this.loop
      ? Math.min(from + 1, this.cards.length - 1)
      : (from + 1) % this.cards.length;
    this.scrollToCard(to);
  }

  prev() {
    if (!this.cards || this.cards.length <= 1) return;
    const from = this.centreIndex();
    const to = this.loop ? Math.max(from - 1, 0) : (from - 1 + this.cards.length) % this.cards.length;
    this.scrollToCard(to);
  }

  scrollToCard(index) {
    const targetCard = this.cards[index];
    if (!targetCard) return;

    const railCenter = this.rail.clientWidth / 2;
    const cardCenter = targetCard.offsetLeft + targetCard.offsetWidth / 2;
    const scrollTarget = cardCenter - railCenter;

    this.rail.scrollTo({
      left: Math.max(0, scrollTarget),
      behavior: this.reduceMotion.matches ? 'auto' : 'smooth'
    });
  }

  onNav(event) {
    const isPrev = event.currentTarget.hasAttribute('data-reel-prev');
    if (isPrev) {
      this.prev();
    } else {
      this.next();
    }
  }

  onScroll() {
    // The recentre waits for the rail to stop moving: writing scrollLeft while
    // a smooth scroll is in flight cancels it, which the shopper would see as
    // the carousel stopping dead halfway.
    if (this.loop) {
      if (this.idleTimer) clearTimeout(this.idleTimer);
      this.idleTimer = setTimeout(() => {
        this.idleTimer = null;
        this.flushRecentre();
      }, 140);
    }

    if (this.navFrame) return;

    this.navFrame = window.requestAnimationFrame(() => {
      this.navFrame = null;
      this.syncCentre();
      this.syncNav();
    });
  }

  syncNav() {
    if (!this.prevBtn || !this.nextBtn) return;
    this.prevBtn.disabled = false;
    this.nextBtn.disabled = false;
  }
}

if (!customElements.get('reel-carousel')) {
  customElements.define('reel-carousel', ReelCarousel);
}

class InstaGrid extends HTMLElement {
  connectedCallback() {
    this.tiles = Array.from(this.querySelectorAll('[data-insta-tile]'));
    if (this.tiles.length === 0) return;

    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.activeTile = null;

    this.initVideos();
    this.bindEvents();
  }

  initVideos() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target.querySelector('video');
          if (!video) return;

          // A tile the shopper paused by hand stays paused when it scrolls
          // back into view — otherwise the observer restarts it while the
          // is-paused class (and so the button icon) still says paused.
          if (
            entry.isIntersecting &&
            !this.reduceMotion.matches &&
            !entry.target.classList.contains('is-paused')
          ) {
            const playPromise = video.play();
            if (playPromise && typeof playPromise.catch === 'function') {
              playPromise.catch(() => {});
            }
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.2 }
    );

    this.tiles.forEach((tile) => this.observer.observe(tile));
  }

  bindEvents() {
    this.tiles.forEach((tile) => {
      const video = tile.querySelector('video');

      tile.addEventListener('click', (e) => {
        // If clicking visit link, allow normal navigation
        if (e.target.closest('[data-insta-visit]')) return;

        // If clicking sound button, toggle mute
        if (e.target.closest('[data-insta-toggle-sound]')) {
          e.preventDefault();
          e.stopPropagation();
          if (video) {
            video.muted = !video.muted;
            tile.classList.toggle('is-unmuted', !video.muted);
          }
          return;
        }

        // If clicking play/pause button
        if (e.target.closest('[data-insta-toggle-play]')) {
          e.preventDefault();
          e.stopPropagation();
          if (video) {
            if (video.paused) {
              video.play();
              tile.classList.remove('is-paused');
            } else {
              video.pause();
              tile.classList.add('is-paused');
            }
          }
          return;
        }

        // Clicking on the tile toggles interactive control overlay
        if (this.activeTile && this.activeTile !== tile) {
          this.activeTile.classList.remove('is-controls-open');
        }

        tile.classList.toggle('is-controls-open');
        this.activeTile = tile.classList.contains('is-controls-open') ? tile : null;
      });
    });

    this.onDocumentClick = (e) => {
      if (!this.contains(e.target) && this.activeTile) {
        this.activeTile.classList.remove('is-controls-open');
        this.activeTile = null;
      }
    };
    document.addEventListener('click', this.onDocumentClick);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this.onDocumentClick) {
      document.removeEventListener('click', this.onDocumentClick);
    }
  }
}

if (!customElements.get('insta-grid')) {
  customElements.define('insta-grid', InstaGrid);
}
